import isEqual from 'react-fast-compare';
import { getIn, setIn } from 'formik';
import { ElementWithMeta } from '../../../types';
import { CustomFieldInputModel } from '../../../elements';
import { InputModel } from '../model';
import { DiffMode } from './types';
import { getPathsFromElements, getRootPath } from './getPathsFromElements';

/**
 * @param {DeepPartial<Model>=} currentModel - values passed via onChange/onSubmit
 * @param {DeepPartial<Model>=} initialModel - initial values
 * @param {ElementWithMeta<Model>[]} elements - elements with metadata used in current Smartform
 * @param {DiffMode} diffMode - comparison type
 *
 * Takes current (modified) model, initial model and compares them.
 * Comparison is based on smartform element (metadata path).
 * So it compares and returns only these values that are defined in smartform elements.
 * Not the entire object.
 *
 * Smartform element metadata paths based on models ( real life example: { model: { engagement: 'uid' } } => 'engagement.uid' ):
 * deep field comparison: 'a', 'nested.b', 'nested.c', 'nested.d', 'nested2', 'nested3' - it returns only these fields
 * shallow field comparison: 'a', 'nested', 'nested2', 'nested3' - it returns only these fields
 *
 * Result:
 * | InitialValues                      | Submitted Values                   | MODE: shallow                      | MODE: deep                         |
 * | ---------------------------------- | ---------------------------------- | ---------------------------------- | ---------------------------------- |
 * | {                                  | {                                  | {                                  | {                                  |
 * |    a: "UNCHANGED",                 |    a: "*CHANGED*",                 |    a: "*CHANGED*",                 |    a: "*CHANGED*",                 |
 * |    nested: {                       |    nested: {                       |    nested: {                       |    nested: {                       |
 * |      b: "UNCHANGED",               |      b: "UNCHANGED",               |      b: "UNCHANGED",               |      c: "*CHANGED*",               |
 * |      c: "UNCHANGED",               |      c: "*CHANGED*",               |      c: "*CHANGED*",               |      d: ["UNCHANGED", "*CHANGED*"] |
 * |      d: ["UNCHANGED", "UNCHANGED"] |      d: ["UNCHANGED", "*CHANGED*"] |      d: ["UNCHANGED", "*CHANGED*"] |    }                               |
 * |      e: "UNCHANGED",               |      e: "UNCHANGED",               |      e: "UNCHANGED",               |    nested3: {                      |
 * |    }                               |    }                               |    }                               |      g: "UNCHANGED",               |
 * |    nested2: {                      |    nested2: {                      |    nested3: {                      |      h: "*CHANGED*",               |
 * |      f: "UNCHANGED",               |      f: "UNCHANGED",               |      g: "UNCHANGED",               |    }                               |
 * |    }                               |    }                               |      h: "*CHANGED*",               |  }                                 |
 * |    nested3: {                      |    nested3: {                      |    }                               |                                    |
 * |      g: "UNCHANGED",               |      g: "UNCHANGED",               |  }                                 |                                    |
 * |      h: "UNCHANGED",               |      h: "*CHANGED*",               |                                    |                                    |
 * |    }                               |    }                               |                                    |                                    |
 * |  }                                 |  }                                 |                                    |                                    |
 */
export function getModelDiff<Model = InputModel>(
  currentModel?: DeepPartial<Model>,
  initialModel?: DeepPartial<Model>,
  elements: ElementWithMeta<Model>[] = [],
  diffMode: DiffMode = 'shallow'
): DeepPartial<Model> | undefined {
  const paths = getPathsFromElements(elements);
  const affectedRootPaths = new Set<string>();

  function addAffectedPath(path: string) {
    affectedRootPaths.add(getRootPath(path));
  }
  const deepDiffResult = paths.reduce<DeepPartial<Model>>((acc: DeepPartial<Model> | undefined, path) => {
    const newValue = getIn(currentModel ?? {}, path);
    const oldValue = getIn(initialModel ?? {}, path);

    if (path.includes('customFields')) {
      const newCFValue = toCustomFieldsComparableObjects(newValue);
      const oldCFValue = toCustomFieldsComparableObjects(oldValue);

      if (isEqual(newCFValue, oldCFValue)) {
        return acc;
      }
      addAffectedPath(path);
      return setIn(acc, path, newValue ?? null);
    }

    // If values are equal and path is not related to personName, skip it.
    // personName is a special case where we need to send the model even if
    // the values are equal as the BE considers personName as a single unit.
    if (isEqual(newValue, oldValue) && !path.startsWith('personName')) {
      return acc;
    }
    addAffectedPath(path);
    return setIn(acc, path, newValue ?? null);
  }, {});

  if (diffMode === 'shallow') {
    return affectedRootPaths.size
      ? Array.from(affectedRootPaths).reduce<DeepPartial<Model>>(
          (acc, path) => setIn(acc, path, getIn(currentModel ?? {}, path) ?? null),
          {}
        )
      : undefined;
  }
  return Object.keys(deepDiffResult).length > 0 ? deepDiffResult : undefined;
}

type CustomFieldValues = CustomFieldInputModel[] | Record<string, CustomFieldInputModel>;
type ComparableCustomField = { value?: string | null; id?: string };

function toCustomFieldsComparableObjects(customFields?: CustomFieldValues): ComparableCustomField[] | undefined {
  if (!customFields) {
    return undefined;
  }
  return (Object.values(customFields ?? []) as CustomFieldInputModel[])
    .filter((cf) => !!cf)
    .map((it) => ({
      value: it.value,
      id: it.definition?.uid
    }));
}
